import { useState, useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import ConstantsStrings from "../../../constants/ConstantsStrings";
import ApiService from "../../../data/ApiService";
import { useLoading } from "../../loadings/WidgetLoading";
import ConstantsTexts from "../../../constants/ConstantsTexts";

// Role configuration for dynamic rendering
const ROLE_CONFIG = [
  { textKey: ConstantsTexts.CODE_216, roleNumber: 3 },
  { textKey: ConstantsTexts.CODE_217, roleNumber: 2 },
  { textKey: ConstantsTexts.CODE_218, roleNumber: 1 },
  { textKey: ConstantsTexts.CODE_219, roleNumber: 0 },
];

export const useWidgetAlertDialogUsers = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.userData);
  const { showLoading, hideLoading } = useLoading();

  // State for users data
  const [users, setUsers] = useState([]);

  // State for filtering
  const [searchText, setSearchText] = useState("");
  const [selectedRoleFilters, setSelectedRoleFilters] = useState(() => {
    const initialFilters = {};
    ROLE_CONFIG.forEach((role) => {
      initialFilters[role.textKey] = true;
    });
    return initialFilters;
  });

  const showLoadingRef = useRef(showLoading);
  const hideLoadingRef = useRef(hideLoading);

  // Generate role options for dropdown
  const generateRoleOptions = useCallback(() => {
    return ROLE_CONFIG.map((role) => ({
      role_number: role.roleNumber,
      role_name: role.textKey,
    }));
  }, []);

  // Utility function to get attribute value from UserAttributes array
  const getAttributeValue = useCallback((userAttributes, attributeName) => {
    if (!userAttributes || !Array.isArray(userAttributes)) return "";
    return userAttributes.find((attr) => attr.Name === attributeName)?.Value || "";
  }, []);

  // Utility function to generate full name from given_name and family_name
  const generateFullName = useCallback((givenName, familyName, email = "") => {
    if (givenName && familyName) {
      return `${givenName} ${familyName}`;
    }
    return givenName || familyName || email.split("@")[0] || "";
  }, []);

  // Get role name by number
  const getRoleNameByNumber = useCallback(
    (roleNumber) => {
      const roleOptions = generateRoleOptions();
      const role = roleOptions.find((option) => option.role_number === parseInt(roleNumber));
      return role ? role.role_name : "ללא הרשאה";
    },
    [generateRoleOptions]
  );

  // Get role option by number
  const getRoleOptionByNumber = useCallback(
    (roleNumber) => {
      const roleOptions = generateRoleOptions();
      return roleOptions.find((option) => option.role_number === parseInt(roleNumber)) || roleOptions[3]; // Default to "ללא הרשאה"
    },
    [generateRoleOptions]
  );

  // Fetch all users
  const fetchUsers = useCallback(async () => {
    showLoadingRef.current();

    try {
      const response = await ApiService.getAllUsers();

      if (response && response.users) {
        // Process users data
        const processedUsers = response.users.map((user) => ({
          ...user,
          UserTypeTerminal: user.UserTypeTerminal || "0",
          fullName: generateFullName(user.given_name, user.family_name, user.email),
          roleOption: getRoleOptionByNumber(user.UserTypeTerminal || "0"),
        }));

        setUsers(processedUsers);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
    } finally {
      hideLoadingRef.current();
    }
  }, [getRoleOptionByNumber, generateFullName]);

  // Fetch users when dialog opens
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleClose = () => {
    dispatch({ type: ConstantsStrings.CLOSE_USERS_DIALOG });
  };

  // Handle role selection for a specific user
  const handleRoleSelect = useCallback(
    async (role, userId) => {
      // Update the user's role in the users array immediately for UI responsiveness
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.sub === userId
            ? {
                ...user,
                UserTypeTerminal: role.role_number.toString(),
                roleOption: role,
              }
            : user
        )
      );

      // Call API to update user data
      try {
        const updateData = {
          sub: userId,
          user_type_terminal: role.role_number.toString(),
        };

        await ApiService.updateUserData(updateData);
      } catch (error) {
        console.error("Error updating user data:", error);

        // Revert the local state changes if the API call fails
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.sub === userId
              ? {
                  ...user,
                  UserTypeTerminal: user.UserTypeTerminal,
                  roleOption: getRoleOptionByNumber(user.UserTypeTerminal),
                }
              : user
          )
        );
      }
    },
    [getRoleOptionByNumber]
  );

  // Handle search text change
  const handleSearchChange = useCallback((text) => {
    setSearchText(text);
  }, []);

  // Handle role filter change
  const handleRoleFilterChange = useCallback((roleName, checked) => {
    setSelectedRoleFilters((prev) => ({
      ...prev,
      [roleName]: checked,
    }));
  }, []);

  // Filter users based on search text and role filters
  const getFilteredUsers = useCallback(() => {
    let filtered = users.filter((user) => {
      // Filter out current user
      if (currentUser?.Username && user.sub === currentUser.Username) {
        return false;
      }

      // Filter by search text
      if (searchText.trim()) {
        const searchLower = searchText.toLowerCase();
        const fullName = user.fullName || "";
        const email = user.email || "";

        if (!fullName.toLowerCase().includes(searchLower) && !email.toLowerCase().includes(searchLower)) {
          return false;
        }
      }

      // Filter by role
      const userRoleName = getRoleNameByNumber(user.UserTypeTerminal);

      if (!selectedRoleFilters[userRoleName]) {
        return false;
      }

      return true;
    });

    return filtered;
  }, [users, currentUser, searchText, selectedRoleFilters, getRoleNameByNumber]);

  // Get role options
  const roleOptions = generateRoleOptions();

  // Process current user data
  const processCurrentUser = useCallback(() => {
    if (!currentUser?.UserAttributes) return null;

    const email = getAttributeValue(currentUser.UserAttributes, "email");

    if (!email) return null;

    const givenName = getAttributeValue(currentUser.UserAttributes, "given_name");
    const familyName = getAttributeValue(currentUser.UserAttributes, "family_name");
    const userTypeTerminal = getAttributeValue(currentUser.UserAttributes, "custom:UserTypeTerminal") || "0";

    return {
      ...currentUser,
      email,
      fullName: generateFullName(givenName, familyName, email),
      UserTypeTerminal: userTypeTerminal,
    };
  }, [currentUser, getAttributeValue, generateFullName]);

  // Get filtered users
  const filteredUsers = getFilteredUsers();

  // Count users with role_number > 0
  const getUsersWithRoleGreaterThanZero = useCallback(() => {
    return users.filter((user) => {
      const roleNumber = parseInt(user.UserTypeTerminal || "0");
      return roleNumber > 0;
    }).length;
  }, [users]);

  return {
    roleOptions,
    ROLE_CONFIG,
    handleRoleSelect,
    handleClose,
    users: filteredUsers,
    currentUser: processCurrentUser(),
    getRoleNameByNumber,
    getRoleOptionByNumber,
    searchText,
    handleSearchChange,
    selectedRoleFilters,
    handleRoleFilterChange,
    usersWithRoleGreaterThanZero: getUsersWithRoleGreaterThanZero(),
  };
};

export default useWidgetAlertDialogUsers;
